import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const PurchaseList = () => {
  const [search, setSearch] = useState("");
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch purchases from API
  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post("/purchase_list.php", {});
      
      if (response.data.head.code === 200) {
        // Transform the data
        const transformedData = response.data.body.map(purchase => ({
          id: purchase.id,
          // purchase_code: purchase.purchase_code,
          date: purchase.date,
          supplier_name: purchase.supplier_name,
          bill_no: purchase.bill_no,
          payment_method: purchase.payment_method,
          item_count: purchase.item_count || 1,
          sub_total: parseFloat(purchase.sub_total),
          gst_amount: parseFloat(purchase.gst_amount),
          grand_total: parseFloat(purchase.grand_total),
          amount: `₹${parseFloat(purchase.grand_total).toLocaleString('en-IN')}`
        }));
        
        setPurchases(transformedData);
      } else if (response.data.head.code === 606) {
        // No purchases found - set empty array
        setPurchases([]);
      } else {
        setError(response.data.head.msg || "Failed to fetch purchases");
      }
    } catch (err) {
      console.error("Error fetching purchases:", err);
      setError("Failed to load purchases. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete purchase handler with API integration
  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete purchase ${id}?`)) {
      return;
    }

    try {
      const response = await api.post("/purchase_delete.php", { id });
      
      if (response.data.head.code === 200) {
        // Remove from local state
        setPurchases(purchases.filter((p) => p.id !== id));
        alert("Purchase deleted successfully!");
      } else {
        alert("Failed to delete purchase: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting purchase:", err);
      alert("Failed to delete purchase. Please try again.");
    }
  };

  // Filter purchases based on search
  const filteredPurchases = purchases.filter((purchase) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      String(purchase.id).toLowerCase().includes(query) ||
      // (purchase.purchase_code && purchase.purchase_code.toLowerCase().includes(query)) ||
      (purchase.supplier_name && purchase.supplier_name.toLowerCase().includes(query)) ||
      (purchase.bill_no && purchase.bill_no.toLowerCase().includes(query)) ||
      (purchase.date && purchase.date.includes(query))
    );
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentPurchases,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredPurchases, 10);

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading purchases...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchPurchases}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Purchase Management</h2>
      
      {/* Search and Add Purchase Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search by supplier name,purchase no"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearch("");
            }}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
          <button
            onClick={() => navigate("/addpurchase")}
            className="btn-top d-inline-flex align-items-center"
            style={{ textDecoration: 'none' }}
          >
            <FaPlus className="me-2" /> Add Purchase
          </button>
        </Col>
      </Row>

      {/* Table Section */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped purchase-table">
            <thead>
              <tr>
                <th>S.No</th>
                {/* <th>Purchase Code</th> */}
                <th>Date</th>
                <th>Purchase No</th>
                <th>Supplier Name</th>
                <th  style={{ textAlign: "center" }}>Amount</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPurchases.map((purchase, index) => (
                <tr key={purchase.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  {/* <td>{purchase.purchase_code}</td> */}
                  <td>{purchase.date}</td>
                  <td>{purchase.bill_no || "N/A"}</td>
                  <td>{purchase.supplier_name}</td>
                  <td  style={{ textAlign: "right" }}>{purchase.amount}</td>
                  <td>
                    <div className="action-icons">
                      <Link
                        to={`/viewpurchase/${purchase.id}`}
                        className="me-2"
                        title="View Purchase"
                      >
                        <FaEye className="view-icon" />
                      </Link>
                      {/* <Link
                        to={`/editpurchase/${purchase.id}`}
                        className="me-2"
                        title="Edit Purchase"
                      >
                        <FaEdit className="edit-icon" />
                      </Link> */}
                      <FaTrash
                        className="delete-icon"
                        title="Delete Purchase"
                        onClick={() => handleDelete(purchase.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {currentPurchases.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    {search ? "No purchases found matching your search" : "No purchases found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </Container>
  );
};

export default PurchaseList;